import {
  ActionBar as GitHubUiActionBar,
  gapFromDensity,
  type ActionBarProps as GitHubUiActionBarProps,
} from '@github-ui/action-bar'
import {testIdProps} from '@github-ui/test-id-props'
import {Box, merge, type SxProp} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import type {ReactElement} from 'react'
import type {ListViewDensityToggle} from './DensityToggle'

// Require actionsLabel if actions are provided, and vice versa.
type ListViewActionBar =
  | {
      /**
       * Description of the controls found in the action bar, e.g., "Issue actions", "Markdown formatting tools".
       * Will be used in a hidden label for accessibility purposes. Defaults to the title for the ListView.
       */
      actionsLabel: GitHubUiActionBarProps['label']
      /**
       * The controls to show in the action bar. These will move into a dropdown menu when the screen isn't big enough to
       * fit them side by side.
       */
      actions: GitHubUiActionBarProps['actions']
    }
  | {actionsLabel?: undefined; actions?: undefined}

export type ListViewActionBarProps = ListViewActionBar & {
  /**
   * Include a toggle to switch between Comfortable and Condensed view modes.
   */
  densityToggle?: ReactElement<typeof ListViewDensityToggle>
} & SxProp &
  Omit<GitHubUiActionBarProps, 'actions' | 'label'>

const defaultSx: SxProp['sx'] = {
  display: 'flex',
  flexDirection: 'row',
  marginLeft: 'auto',
  alignItems: 'center',
}

export const ListViewActionBar = ({
  actions,
  sx,
  children,
  density,
  actionsLabel,
  densityToggle,
}: ListViewActionBarProps) => {
  const nonCollapsibleActions = (
    <>
      {children}
      {densityToggle}
    </>
  )

  if (actions && actions.length > 0) {
    return (
      <GitHubUiActionBar
        label={actionsLabel?.trim()}
        actions={actions}
        density={density}
        {...testIdProps('list-view-actions')}
        sx={sx}
      >
        {nonCollapsibleActions}
      </GitHubUiActionBar>
    )
  }

  const actionsStyles = sx ? merge<BetterSystemStyleObject>(defaultSx, sx) : defaultSx

  return (
    <Box
      {...testIdProps('list-view-actions')}
      sx={merge<BetterSystemStyleObject>(actionsStyles, {gap: gapFromDensity(density)})}
    >
      {nonCollapsibleActions}
    </Box>
  )
}

try{ ListViewActionBar.displayName ||= 'ListViewActionBar' } catch {}